import { css } from '@emotion/react'
import {
  DataTable,
  DataTableRenderProps,
  HFlow,
  Table,
  TableCell,
  TableFilledBody,
  TableHead,
  TableHeader,
  TableRow,
  Text,
  Theme,
  useTheme,
} from 'bold-ui'
import { InfoIcon } from 'components/InfoIcon'
import { CriterioPontuadoVulnerabilidadeFamiliar } from 'graphql/types.generated'

type Item = CriterioPontuadoVulnerabilidadeFamiliar

interface CriteriosPontuadosVulnerabilidadeFamiliarTableProps {
  criterios: ReadonlyArray<CriterioPontuadoVulnerabilidadeFamiliar>
}

export function CriteriosPontuadosVulnerabilidadeFamiliarTable(
  props: Readonly<CriteriosPontuadosVulnerabilidadeFamiliarTableProps>
) {
  const { criterios } = props
  const theme = useTheme()
  const styles = createStyles(theme)

  const somaTotal = criterios.reduce((acc, criterio) => acc + criterio.resultado, 0)

  const ID_INDICADOR_TO_TOOLTIP_MSG: Map<string, string> = new Map([
    ['64', 'Se todos ausentes: 0 \n Se qualquer deles presente: 3'],
    ['60', 'Mais moradores que cômodos: 3 \n Moradores = cômodos: 2 \n Menos moradores que cômodos: 0'],
  ])

  const renderDescricao = (row: Item) => {
    const tooltip = ID_INDICADOR_TO_TOOLTIP_MSG.get(row.id)
    return (
      <HFlow hSpacing={0.5} alignItems='center'>
        <Text>{row.descricao}</Text>
        {tooltip && <InfoIcon icon='infoCircleFilled' text={tooltip} tooltipStyle={styles.tooltipMsg} />}
      </HFlow>
    )
  }

  const renderMoradores = (row: Item) => row.numeroMoradores ?? '-'
  const renderPeso = (row: Item) => row.peso
  const renderResultado = (row: Item) => row.resultado

  const renderTable = ({ columns, rows, getHeaderProps, ...rest }: DataTableRenderProps) => (
    <Table {...rest}>
      <TableHead>
        <TableRow style={styles.header}>
          {columns.map((col) => (
            <TableHeader key={col.name} {...getHeaderProps(col)}>
              {col.header}
            </TableHeader>
          ))}
        </TableRow>
      </TableHead>
      <TableFilledBody rows={rows} columns={columns} />
      <TableRow>
        <TableCell colSpan={2}></TableCell>
        <TableCell style={styles.footerCell}>
          <Text>Soma total</Text>
        </TableCell>
        <TableCell style={styles.footerCell}>
          <Text>{somaTotal}</Text>
        </TableCell>
      </TableRow>
    </Table>
  )

  return (
    <DataTable<Item>
      columns={[
        {
          name: 'descricao',
          header: 'Critérios pontuados na Escala Coelho-Savassi',
          render: renderDescricao,
          style: styles.colunaDescricao,
        },
        { name: 'moradores', header: 'Moradores', render: renderMoradores, style: styles.colunaValores },
        { name: 'peso', header: 'Peso do critério', render: renderPeso, style: styles.colunaValores },
        { name: 'resultado', header: 'Resultado', render: renderResultado, style: styles.colunaValores },
      ]}
      rows={criterios}
      render={renderTable}
    />
  )
}

const createStyles = (theme: Theme) => ({
  header: css`
    background-color: ${theme.pallete.gray.c90};
  `,
  colunaDescricao: css`
    min-width: 10rem;
  `,
  colunaValores: css`
    text-align: center;
    min-width: 5rem;
    padding: 0rem;
  `,
  footerCell: css`
    text-align: center;
    font-weight: bold;
  `,
  tooltipMsg: css`
    white-space: pre-line;
    text-align: left;
  `,
})
